import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, Clock, Phone, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SiteLayout } from "@/components/site/Layout";
import { useSeo } from "@/context/ContentContext";
import { api, gbp } from "@/lib/api";

const ServiceRow = ({ s, slug }) => (
  <div className="flex items-center justify-between gap-4 py-5 border-b border-border last:border-0" data-testid={`clinic-service-${s.id}`}>
    <div>
      <p className="font-display font-bold">{s.name}</p>
      <p className="text-sm text-muted-foreground">{s.duration} min · {s.description}</p>
    </div>
    <div className="flex items-center gap-4 shrink-0">
      <span className="font-semibold">{gbp(s.price)}</span>
      <Button size="sm" className="rounded-full" asChild data-testid={`service-book-button-${s.id}`}>
        <Link to={`/book/${slug}?service=${s.id}`}>Book</Link>
      </Button>
    </div>
  </div>
);

export default function ClinicPage() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get(`/clinics/${slug}`).then((r) => setC(r.data)).catch(() => setErr(true));
  }, [slug]);

  useSeo("clinic", { clinic: c?.name || "Clinic", city: c?.city || "" });
  if (err) return <SiteLayout><p className="max-w-7xl mx-auto p-10" data-testid="clinic-not-found">Clinic not found.</p></SiteLayout>;
  if (!c) return <SiteLayout><div className="p-20 flex justify-center"><Loader2 className="animate-spin text-primary" /></div></SiteLayout>;

  return (
    <SiteLayout>
      <section className="relative h-72 sm:h-96 overflow-hidden">
        <img src={c.image} alt={c.name} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A34] via-[#1E3A34]/40 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-end pb-10 text-white fade-up">
          <span className="w-fit rounded-full bg-accent px-3 py-1 text-xs font-semibold">{c.discipline_name}</span>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mt-3" data-testid="clinic-name">{c.name}</h1>
          <p className="flex items-center gap-1.5 mt-2 text-white/85 text-sm"><MapPin className="h-4 w-4" />{c.address || c.city}</p>
        </div>
      </section>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-[1fr_340px] gap-10">
        <div className="space-y-14">
          <section>
            <p className="eyebrow">Treatments</p>
            <h2 className="text-2xl font-bold tracking-tight mt-2 mb-4">Choose a treatment</h2>
            <div className="rounded-2xl bg-card border border-border px-5">
              {c.services.map((s) => <ServiceRow key={s.id} s={s} slug={c.slug} />)}
              {c.services.length === 0 && <p className="py-6 text-muted-foreground">No treatments listed yet.</p>}
            </div>
          </section>
          <section>
            <p className="eyebrow">Team</p>
            <h2 className="text-2xl font-bold tracking-tight mt-2 mb-6">Practitioners</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              {c.practitioners.map((p) => (
                <div key={p.id} className="flex gap-4 rounded-2xl bg-card border border-border p-4" data-testid={`practitioner-card-${p.id}`}>
                  <img src={p.image} alt={p.name} className="h-20 w-20 rounded-xl object-cover" />
                  <div>
                    <p className="font-display font-bold">{p.name}</p>
                    <p className="text-sm text-primary">{p.title}</p>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{p.bio}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
          <section>
            <p className="eyebrow">About</p>
            <p className="mt-3 text-slate-600 leading-relaxed whitespace-pre-line">{c.description || "No description yet."}</p>
          </section>
        </div>
        <aside className="lg:sticky lg:top-24 self-start rounded-2xl bg-card border border-border p-6 space-y-4 shadow-sm">
          <p className="font-display font-bold text-lg">Book an appointment</p>
          {c.min_price != null && <p className="text-sm text-muted-foreground">Treatments from <span className="font-semibold text-foreground">{gbp(c.min_price)}</span></p>}
          <Button className="w-full rounded-full h-11" asChild data-testid={`clinic-book-now-button-${c.id}`}>
            <Link to={`/book/${c.slug}`}>Book now <ArrowRight className="h-4 w-4 ml-2" /></Link>
          </Button>
          <div className="space-y-2 text-sm text-slate-600 pt-2 border-t border-border">
            {c.opening_hours && <p className="flex gap-2"><Clock className="h-4 w-4 mt-0.5 text-primary" />{c.opening_hours}</p>}
            {c.phone && <p className="flex gap-2"><Phone className="h-4 w-4 mt-0.5 text-primary" />{c.phone}</p>}
            <p className="flex gap-2"><MapPin className="h-4 w-4 mt-0.5 text-primary" />{c.address || c.city}</p>
            <p className="text-xs text-muted-foreground pt-1" data-testid="clinic-cancellation-policy">Free cancellation with full refund up to {c.cancellation_cutoff_hours} hours before your appointment.</p>
          </div>
        </aside>
      </div>
    </SiteLayout>
  );
}
